import { ref } from 'vue';
import { App, Plugin, watch } from 'vue';
import { echo, configureEcho } from '@laravel/echo-vue';
import type Echo from 'laravel-echo';

import type { User } from '@lib/types';

import { useToasterStore } from '@lib/plugins/toasts/toasts';

type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'reconnecting';

export let echoInstance = null as Echo<'pusher'> | null;
export const connectionStatus = ref<ConnectionStatus>('disconnected');
export const reconnectAttempts = ref(0);

// Id of the user the notification channels are currently bound to. The auth watcher fires
// again for the same user (token refresh, /auth/user fetch, context switch) and Echo hands
// back the cached channel instance, so re-binding there would stack duplicate handlers.
let subscribedUserId: User['id'] | null = null;

export const AppBroadcastPlugin: Plugin = {
	install: (app: App /*options?: ApplicationOptions*/) => {
		// We are not in a vue instance yet, injection based useComposables do not work, pinja stores do
		const auth = app.config.globalProperties.$auth;
		const toasterStore = useToasterStore();

		const tlsEnabled = (window.env?.broadcasting?.reverb?.scheme ?? 'https') === 'https';

		// Init Reverb
		configureEcho({
			broadcaster: 'reverb',
			key: window.env?.broadcasting?.reverb?.app_key,
			wsHost: window.env?.broadcasting?.reverb?.host,
			wsPort:
				!tlsEnabled ? Number.parseInt(window.env?.broadcasting?.reverb?.port ?? '80') : undefined,
			wssPort:
				tlsEnabled ? Number.parseInt(window.env?.broadcasting?.reverb?.port ?? '443') : undefined,
			wsPath: window.env?.broadcasting?.reverb?.base_path || '',
			forceTLS: tlsEnabled,
			// Both names are required. pusher-js builds its TLS strategy on the transport it
			// calls "ws" and picks wss:// through useTLS/forceTLS
			// Listing "wss" alone leaves the strategy with no usable transport
			enabledTransports: ['ws', 'wss'],
			// Read the token per subscription. A copy taken at login goes stale on the 50 minute
			// refresh (or a refresh in another tab), which blacklists it, and every channel
			// subscribed afterwards, e.g. the event channel of the map, got a 403
			channelAuthorization: {
				endpoint: '/broadcasting/auth',
				transport: 'ajax',
				headersProvider: () => (auth.token() ? { Authorization: `Bearer ${auth.token()}` } : {}),
			},
		});

		// Watch for setup of app
		watch(
			() => auth.user(),
			async (user: User) => {
				// User logged in
				if (user) {
					if (!echoInstance) {
						echoInstance = echo() as Echo<'pusher'>;
						const pusher = (echoInstance?.connector).pusher;

						// Register Listeners
						pusher.connection.bind('connected', () => {
							connectionStatus.value = 'connected';
							reconnectAttempts.value = 0; // reset on success
						});

						pusher.connection.bind('disconnected', () => {
							connectionStatus.value = 'disconnected';
						});

						pusher.connection.bind('unavailable', () => {
							connectionStatus.value = 'error';
						});

						pusher.connection.bind('failed', () => {
							connectionStatus.value = 'error';
						});

						pusher.connection.bind('connecting_in', (delay: number) => {
							reconnectAttempts.value++;
							connectionStatus.value = 'reconnecting';
							console.warn(
								`Reconnecting to Echo/Reverb server in ${delay} seconds (attempt ${reconnectAttempts.value})`,
							);
						});
					}

					// Already bound for this user, nothing left to do
					if (subscribedUserId === user.id) return;

					// Drop the channels of the previous user before binding the new ones
					if (subscribedUserId !== null) {
						echoInstance.leaveAllChannels();
					}
					subscribedUserId = user.id;

					// Connect to public Echo Channel
					echoInstance.channel('app').notification((notification: any) => {
						toasterStore.addToast(
							'Message received',
							notification.message,
							notification.icon || 'fa fa-info-circle',
							notification.type || '',
						);
					});

					// Conntect to private Echo Channel for the current user
					echoInstance.private(`App.Models.User.${user.id}`).notification((notification: any) => {
						if (notification.message_type == 'success') {
							toasterStore.addSuccessToast('Broadcast', notification.message);
						} else if (notification.message_type == 'error') {
							toasterStore.addErrorToast('Broadcast', notification.message);
						} else {
							toasterStore.addToast('Broadcast', notification.message);
						}
					});
				}
				// User logged out
				else {
					subscribedUserId = null;

					if (echoInstance) {
						echoInstance.leaveAllChannels();
					}
				}
			},
		);
	},
};

export default AppBroadcastPlugin;
